// src/models/SickLeaveReportModel.js

/**
 * SickLeaveReportModel
 * Represents a single sick leave day entry used for report display and CSV export.
 * Follows the same structural pattern as VacationReportModel and TimeEntryReportModel.
 *
 * Source collection: companies/{companyId}/sickLeave/{sickLeaveId}
 *
 * Note: staffName is not stored in Firestore and must be resolved
 * externally via the staffMembers array before constructing this model.
 *
 * Note: durationMinutes is intentionally excluded from this model.
 * Sick leave reports show exact dates only (days-based, v1).
 * Each Firestore document represents one sick leave day regardless
 * of its durationMinutes value.
 *
 * This file is separate from SickLeaveModels.js, which is the full
 * domain/management model used by the sick leave management feature.
 * This model is a display-only DTO used exclusively by the report pipeline.
 */
export class SickLeaveReportModel {
  /**
   * @param {string} staffId   - Firestore staff document ID
   * @param {string} staffName - Resolved staff name (not stored in Firestore)
   * @param {string} date      - Sick leave date as YYYY-MM-DD string
   */
  constructor(staffId, staffName, date) {
    this.staffId   = staffId;
    this.staffName = staffName || '';
    this.date      = date; // YYYY-MM-DD string
  }

  // ---------------------------------------------------------------------------
  // Display Methods
  // ---------------------------------------------------------------------------

  /**
   * Returns formatted date string in DD.MM.YYYY format.
   * Parses the stored YYYY-MM-DD string directly to avoid UTC timezone shifts
   * that would occur when passing the string to new Date().
   */
  getFormattedDate() {
    if (!this.date) return '';
    const [year, month, day] = this.date.split('-');
    return `${day}.${month}.${year}`;
  }

  // ---------------------------------------------------------------------------
  // Static Factory
  // ---------------------------------------------------------------------------

  /**
   * Creates a SickLeaveReportModel from a SickLeaveModel instance.
   * staffName must be supplied externally as it is not stored in Firestore.
   *
   * @param {Object} sickLeaveModel - Source SickLeaveModel instance
   * @param {string} staffId        - Firestore staff document ID
   * @param {string} staffName      - Resolved staff name from the staffMembers array
   * @returns {SickLeaveReportModel}
   */
  static fromSickLeaveModel(sickLeaveModel, staffId, staffName) {
    return new SickLeaveReportModel(
      staffId,
      staffName || '',
      sickLeaveModel.date
    );
  }

  // ---------------------------------------------------------------------------
  // Static Utilities
  // ---------------------------------------------------------------------------

  /**
   * Groups an array of SickLeaveReportModel entries by staffId.
   * Returns a plain object keyed by staffId where each value is an array
   * of that staff member's entries sorted by date ascending.
   *
   * @param {SickLeaveReportModel[]} entries
   * @returns {Object.<string, SickLeaveReportModel[]>}
   */
  static groupByStaff(entries) {
    const groups = entries.reduce((acc, entry) => {
      if (!acc[entry.staffId]) {
        acc[entry.staffId] = [];
      }
      acc[entry.staffId].push(entry);
      return acc;
    }, {});

    Object.keys(groups).forEach(staffId => {
      groups[staffId].sort((a, b) => a.date.localeCompare(b.date));
    });

    return groups;
  }

  /**
   * Builds one summary row per staff member for the Sick Leave Summary report type.
   * Each row contains the staff name and total sick day count.
   * Every entry counts as one day. Duration is not tracked in sick leave reports.
   * Rows are sorted alphabetically by staffName for consistent output.
   *
   * @param {SickLeaveReportModel[]} entries
   * @returns {Array<{
   *   staffId:    string,
   *   staffName:  string,
   *   totalDays:  number
   * }>}
   */
  static buildSummaryRows(entries) {
    const grouped = SickLeaveReportModel.groupByStaff(entries);

    const rows = Object.entries(grouped).map(([staffId, staffEntries]) => {
      const staffName = staffEntries[0]?.staffName || '';
      const totalDays = staffEntries.length;

      return {
        staffId,
        staffName,
        totalDays
      };
    });

    return rows.sort((a, b) => a.staffName.localeCompare(b.staffName));
  }
}

export default SickLeaveReportModel;